import React, { useEffect, useMemo, useState } from 'react';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useAuth } from '../../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ServiceImportExport from './import-export/ServiceImportExport';
import TutorialBanner from '../../tutorial/ui/TutorialBanner';
import '../../services/styles/services.css';
import './manage-services-v2/manage-services-v2.css';
import ServiceCard from './manage-services-v2/ServiceCard';
import ManageServicesLocationBar from './manage-services-v2/ManageServicesLocationBar';
import ActionButton from '../../../shared/components/action-button/ActionButton';
import { fetchCompanyStaff } from '../../../shared/booking/service-staff/serviceStaffRouting';
import { createService, listServices } from '../../../api/companyRecords';
import useCompanyLocations from '../../../locations/hooks/useCompanyLocations';
import { entityAssignedToLocation } from '../../../locations/locationTypes';
import { buildServiceSlugFields, getServicePathSegment } from '../../services/utils/serviceSlug';
import EmployeeSelfServiceAssignmentSettingsRow from '../../services/service-staff-assignment/EmployeeSelfServiceAssignmentSettingsRow';

const ManageServices = () => {
  const { companyData } = useAuth();
  const { activeLocations, defaultLocation, showLocationStep, loading: locationsLoading } = useCompanyLocations({
    companyId: companyData?.id,
    companyData,
    activeOnly: true,
  });
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [showAllServices, setShowAllServices] = useState(false);
  const muiTheme = useMuiTheme?.();
  const isDark = muiTheme?.palette?.mode === 'dark';
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const location = useLocation();
  const fromTutorial = Boolean(location.state?.fromTutorial);
  const [importLoading, setImportLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('name_asc');
  const [staffOptions, setStaffOptions] = useState([]);

  const selectedLocation = useMemo(
    () => activeLocations.find((entry) => entry.id === selectedLocationId) || null,
    [activeLocations, selectedLocationId]
  );

  useEffect(() => {
    if (location.state?.selectedLocationId) {
      setSelectedLocationId(location.state.selectedLocationId);
    }
  }, [location.state?.selectedLocationId]);

  useEffect(() => {
    if (!showLocationStep || locationsLoading) return;

    setSelectedLocationId((current) => {
      if (current && activeLocations.some((entry) => entry.id === current)) {
        return current;
      }
      return defaultLocation?.id || activeLocations[0]?.id || '';
    });
  }, [showLocationStep, locationsLoading, defaultLocation, activeLocations]);

  const servicesForView = useMemo(() => {
    if (!showLocationStep || showAllServices || !selectedLocationId) {
      return services;
    }

    return services.filter((service) => entityAssignedToLocation(service, selectedLocationId));
  }, [services, showLocationStep, showAllServices, selectedLocationId]);

  const goToNewService = () => {
    const params = new URLSearchParams();
    if (showLocationStep && selectedLocationId && !showAllServices) {
      params.set('locationId', selectedLocationId);
    }
    const query = params.toString();
    navigate(`/app/overview/services/new${query ? `?${query}` : ''}`);
  };

  const cssVars = useMemo(() => {
    // Force the same token set used by `services.css` based on the actual MUI mode,
    // so this page can't look "dark" when desktop settings is in light mode.
    if (isDark) {
      return {
        '--bg-0': '#1a1d24',
        '--bg-50': '#1a1d24',
        '--bg-paper': '#1a1d24',
        '--text-900': '#e5e7eb',
        '--text-700': '#e5e7eb',
        '--text-500': '#9ca3af',
        '--text-primary': '#e5e7eb',
        '--text-secondary': '#9ca3af',
        '--border-200': 'rgba(255,255,255,0.12)',
        '--border-300': 'rgba(255,255,255,0.18)',
        '--border-primary': 'rgba(255,255,255,0.12)',
      };
    }
    return {
      '--bg-0': '#ffffff',
      '--bg-50': '#f8fafc',
      '--bg-paper': '#ffffff',
      '--text-900': '#0f172a',
      '--text-700': '#334155',
      '--text-500': '#64748b',
      '--text-primary': '#0f172a',
      '--text-secondary': '#64748b',
      '--border-200': '#e5e7eb',
      '--border-300': '#d1d5db',
      '--border-primary': '#e5e7eb',
    };
  }, [isDark]);

  useEffect(() => {
    if (!companyData?.id) return undefined;

    let cancelled = false;
    const loadServices = async () => {
      try {
        const servicesData = (await listServices({ companyId: companyData.id }))
          .filter((service) => !service._init)
          .map((service) => ({
            ...service,
            addOnsCount: service.addOns?.length || 0,
            packagesCount: service.packages?.length || 0
          }));
        if (!cancelled) setServices(servicesData);
      } catch (fetchError) {
        console.error('Error fetching services:', fetchError);
        if (!cancelled) setError('Failed to load services');
      }
    };

    loadServices();
    return () => {
      cancelled = true;
    };
  }, [companyData?.id]);

  useEffect(() => {
    const loadStaffOptions = async () => {
      const ownerId = companyData?.ownerId || companyData?.createdBy;
      if (!companyData?.id) {
        setStaffOptions([]);
        return;
      }

      try {
        const nextStaffOptions = await fetchCompanyStaff({
          companyId: companyData.id,
          ownerId,
        });
        setStaffOptions(nextStaffOptions);
      } catch (staffError) {
        console.error('Error loading service staff options:', staffError);
      }
    };

    loadStaffOptions();
  }, [companyData?.id, companyData?.ownerId, companyData?.createdBy]);

  const visibleServices = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const list = Array.isArray(servicesForView) ? [...servicesForView] : [];

    const filtered = q
      ? list.filter((s) => String(s?.name || '').toLowerCase().includes(q))
      : list;

    const toMs = (v) => {
      const ms = Date.parse(String(v || ''));
      return Number.isFinite(ms) ? ms : 0;
    };

    filtered.sort((a, b) => {
      if (sortKey === 'name_desc') return String(b?.name || '').localeCompare(String(a?.name || ''));
      if (sortKey === 'updated_desc') return toMs(b?.updatedAt) - toMs(a?.updatedAt);
      if (sortKey === 'created_desc') return toMs(b?.createdAt) - toMs(a?.createdAt);
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });

    return filtered;
  }, [servicesForView, searchTerm, sortKey]);

  const hiddenByLocationCount = showLocationStep && !showAllServices && selectedLocationId
    ? Math.max(0, services.length - servicesForView.length)
    : 0;

  const handleImportServices = async (importedServices) => {
    setImportLoading(true);
    setError('');
    
    try {
      if (!companyData?.id) {
        throw new Error('No company ID available');
      }

      // Check for duplicate names in imported services
      const duplicateNames = importedServices.filter((importService, index) => 
        importedServices.findIndex(service => 
          service.name.toLowerCase() === importService.name.toLowerCase()
        ) !== index
      );

      if (duplicateNames.length > 0) {
        throw new Error('Duplicate service names found in import file');
      }

      // Check for conflicts with existing services
      const conflicts = importedServices.filter(importService => 
        services.some(existingService => 
          existingService.name.toLowerCase() === importService.name.toLowerCase()
        )
      );

      if (conflicts.length > 0) {
        throw new Error(`Conflicting service names: ${conflicts.map(s => s.name).join(', ')}`);
      }

      const existingForSlug = [...services];
      const importedSlugFields = [];

      for (const service of importedServices) {
        const slugFields = buildServiceSlugFields(service.name, [...existingForSlug, ...importedSlugFields]);
        importedSlugFields.push({
          id: `import-${importedSlugFields.length}`,
          name: service.name,
          slug: slugFields.slug,
        });
        await createService({
          companyId: companyData.id,
          service: {
            name: service.name,
            description: service.description || '',
            duration: service.duration,
            bufferTime: service.bufferTime || 0,
            price: service.price,
            imageUrl: service.imageUrl || '',
            addOns: service.addOns || [],
            packages: service.packages || [],
            employeeIds: service.employeeIds || [],
            locationIds: selectedLocationId ? [selectedLocationId] : [],
            ...slugFields,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            addOnsCount: service.addOns?.length || 0,
            packagesCount: service.packages?.length || 0
          },
        });
      }

      const nextServices = await listServices({ companyId: companyData.id });
      setServices(nextServices.filter((service) => !service._init).map((service) => ({
        ...service,
        addOnsCount: service.addOns?.length || 0,
        packagesCount: service.packages?.length || 0
      })));
      setSuccess('Services imported successfully');
    } catch (err) {
      console.error("Error importing services:", err);
      setError(err.message || "Failed to import services");
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className={`svc-page ms2-page ${isDark ? 'is-dark' : ''}`} style={cssVars}>
      {fromTutorial && (
        <TutorialBanner description="Create services your business offers with duration and price. Clients can then book these services." onDismiss={() => {}} />
      )}

      <div className="ms2-header">
        <div className="ms2-titleBlock">
          <h1>Manage Services</h1>
          <p>
            {showLocationStep && selectedLocation
              ? `Services offered at ${selectedLocation.name}. Assign staff who work at this location.`
              : 'Create services that clients can book. Click a service to edit it.'}
          </p>
        </div>
        <div className="ms2-pill">
          <span>{showLocationStep && selectedLocation ? selectedLocation.name : 'Services'}</span>
          {' '}
          {showLocationStep && !showAllServices ? visibleServices.length : services.length}
        </div>
      </div>

      {showLocationStep ? (
        <ManageServicesLocationBar
          locations={activeLocations}
          selectedLocationId={selectedLocationId}
          onSelectLocation={setSelectedLocationId}
        />
      ) : null}

      <EmployeeSelfServiceAssignmentSettingsRow />

      <div className="ms2-toolbar app-action-toolbar">
        <ServiceImportExport services={services} onImport={handleImportServices} disabled={importLoading} />

        {showLocationStep ? (
          <label className="ms2-filterToggle">
            <input
              type="checkbox"
              checked={showAllServices}
              onChange={(event) => setShowAllServices(event.target.checked)}
            />
            Show all services
          </label>
        ) : null}

        <div className="ms2-grow">
          <input
            className="ms2-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search services…"
            aria-label="Search services"
          />
        </div>

        <select className="ms2-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)} aria-label="Sort services">
          <option value="name_asc">Sort: Name (A–Z)</option>
          <option value="name_desc">Sort: Name (Z–A)</option>
          <option value="updated_desc">Sort: Recently updated</option>
          <option value="created_desc">Sort: Recently created</option>
        </select>

        <ActionButton variant="primary" onClick={goToNewService}>
          New Service
        </ActionButton>
      </div>

      {hiddenByLocationCount > 0 ? (
        <div className="ms2-contextBanner" role="status">
          <strong>{hiddenByLocationCount}</strong> service{hiddenByLocationCount === 1 ? '' : 's'} not offered at{' '}
          {selectedLocation?.name}. Turn on <strong>Show all services</strong> to find and edit them.
        </div>
      ) : null}

      {error && <div className="svc-alert svc-alert--error" style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="svc-alert svc-alert--success" style={{ marginBottom: 12 }}>{success}</div>}

      <div className="ms2-content">
        <div className="ms2-circleGrid">
          {visibleServices.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              locations={activeLocations}
              staffOptions={staffOptions}
              contextLocationId={showLocationStep && !showAllServices ? selectedLocationId : ''}
              onOpen={() => navigate(`/app/overview/services/${getServicePathSegment(service)}`)}
            />
          ))}
        </div>

        {services.length === 0 && (
          <div className="svc-empty">
            <div style={{ fontSize: 44, marginBottom: 6 }}>🏷️</div>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>No Services Added Yet</div>
            <div style={{ marginBottom: 16, color: 'var(--text-500)' }}>Start by adding your first service.</div>
            <ActionButton variant="primary" onClick={goToNewService}>
              New Service
            </ActionButton>
          </div>
        )}

        {services.length > 0 && visibleServices.length === 0 && (
          <div className="svc-empty">
            <div style={{ fontSize: 44, marginBottom: 6 }}>📍</div>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              No services at {selectedLocation?.name || 'this location'}
            </div>
            <div style={{ marginBottom: 16, color: 'var(--text-500)' }}>
              Add a new service for this location, or show all services to offer an existing one here.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <ActionButton variant="primary" onClick={goToNewService}>
                New service for {selectedLocation?.name || 'location'}
              </ActionButton>
              {hiddenByLocationCount > 0 ? (
                <ActionButton type="button" onClick={() => setShowAllServices(true)}>
                  Show all services
                </ActionButton>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageServices;